import React, { useContext } from "react";
import { motion } from "framer-motion";
import emoji from "react-easy-emoji";
import StyleContext from "../../contexts/StyleContext";
import { skillsSection, moreSkills } from "../../portfolio";

export default function SkillsModern() {
  const { isDark } = useContext(StyleContext);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5 },
    },
  };

  const skillCardVariants = {
    hidden: { opacity: 0, scale: 0.8 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: { duration: 0.5 },
    },
  };

  return (
    <div className="relative py-20 md:py-32 overflow-hidden" id="skills">
      {/* Background Gradient */}
      <div className="absolute inset-0 bg-gradient-to-br from-gray-50 via-purple-50 to-pink-50 dark:from-gray-900 dark:via-purple-900/20 dark:to-pink-900/20" />

      {/* Floating Orb */}
      <motion.div
        className="absolute top-40 right-0 w-96 h-96 bg-gradient-to-r from-purple-400 to-pink-400 rounded-full opacity-20 blur-3xl"
        animate={{
          scale: [1, 1.2, 1],
          x: [0, -50, 0],
          y: [0, 30, 0],
        }}
        transition={{
          duration: 8,
          repeat: Infinity,
          ease: "easeInOut",
        }}
      />

      <motion.div
        className="relative z-10 container mx-auto px-4"
        variants={containerVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-100px" }}
      >
        {/* Section Header */}
        <motion.div variants={itemVariants} className="text-center mb-16">
          <motion.div
            className="inline-block mb-4"
            whileHover={{ scale: 1.05 }}
          >
            <span className="px-4 py-2 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-full text-sm font-semibold shadow-lg">
              Skills & Expertise
            </span>
          </motion.div>
          <h2 className={`text-4xl md:text-5xl lg:text-6xl font-bold mb-6 ${
            isDark ? "text-white" : "text-gray-900"
          }`}>
            {skillsSection.title2}
          </h2>
        </motion.div>

        {/* What I Do Cards */}
        <motion.div
          variants={containerVariants}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-20"
        >
          {skillsSection.skills.map((skill, index) => (
            <motion.div
              key={index}
              variants={skillCardVariants}
              whileHover={{
                scale: 1.05,
                transition: { duration: 0.2 }
              }}
              className="glass backdrop-blur-xl rounded-2xl p-6 bg-white/40 dark:bg-gray-800/40 border border-white/20 dark:border-gray-700/20 shadow-xl hover:shadow-2xl hover:shadow-purple-500/20 transition-all duration-300"
            >
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center text-2xl">
                  ⚡
                </div>
                <p className={`text-base leading-relaxed ${
                  isDark ? "text-gray-300" : "text-gray-700"
                }`}>
                  {skill}
                </p>
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* Technologies Section */}
        <motion.div variants={itemVariants} className="max-w-6xl mx-auto">
          {/* Section Title */}
          <div className="text-center mb-12">
            <h3 className={`text-3xl md:text-4xl font-bold mb-4 ${
              isDark ? "text-white" : "text-gray-900"
            }`}>
              {skillsSection.title}
            </h3>
            <p className={`text-lg md:text-xl max-w-3xl mx-auto ${
              isDark ? "text-gray-400" : "text-gray-600"
            }`}>
              {skillsSection.subTitle}
            </p>
          </div>

          {/* Tech Stack Grid */}
          <motion.div
            variants={containerVariants}
            className="glass backdrop-blur-xl rounded-3xl p-6 md:p-10 bg-white/40 dark:bg-gray-800/40 border border-white/20 dark:border-gray-700/20 shadow-2xl"
          >
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-4 md:gap-6">
              {moreSkills.skills.map((skill, index) => (
                <motion.div
                  key={index}
                  variants={skillCardVariants}
                  whileHover={{
                    scale: 1.1,
                    rotate: [0, -5, 5, 0],
                    transition: { duration: 0.3 }
                  }}
                  whileTap={{ scale: 0.95 }}
                  className="group relative"
                >
                  <div className={`relative rounded-2xl p-3 md:p-4 transition-all duration-300 ${
                    isDark
                      ? "bg-gray-700/50 hover:bg-gray-700"
                      : "bg-white/60 hover:bg-white"
                  } border ${
                    isDark
                      ? "border-gray-600/50 hover:border-purple-500/50"
                      : "border-gray-200/50 hover:border-purple-400/50"
                  } shadow-lg hover:shadow-xl`}>
                    {/* Icon */}
                    <div className="flex flex-col items-center gap-2">
                      <div className="w-10 h-10 md:w-12 md:h-12 flex items-center justify-center">
                        <img
                          src={skill.image.default || skill.image}
                          alt={skill.name}
                          className="w-full h-full object-contain transition-transform duration-300 group-hover:scale-110"
                        />
                      </div>
                      {/* Name */}
                      <p className={`text-xs font-medium text-center break-words w-full transition-colors duration-300 ${
                        isDark
                          ? "text-gray-300 group-hover:text-white"
                          : "text-gray-700 group-hover:text-gray-900"
                      }`}>
                        {skill.name}
                      </p>
                    </div>

                    {/* Hover Glow Effect */}
                    <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-purple-500/0 to-pink-500/0 group-hover:from-purple-500/20 group-hover:to-pink-500/20 transition-all duration-300 pointer-events-none" />
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </motion.div>

        {/* Bottom Decorative Element */}
        <motion.div
          variants={itemVariants}
          className="mt-20 flex justify-center"
        >
          <div className="flex items-center gap-2">
            <div className="h-px w-20 bg-gradient-to-r from-transparent via-purple-500 to-transparent" />
            <div className="w-2 h-2 rounded-full bg-purple-500" />
            <div className="h-px w-20 bg-gradient-to-r from-transparent via-pink-500 to-transparent" />
          </div>
        </motion.div>
      </motion.div>
    </div>
  );
}
